import { ChangeDetectionStrategy, Component, computed, input, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { catchError, map, Observable, of, ReplaySubject, share, switchMap, tap, timer } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@coin/shared/core/util-environments';
import { EAGER_EMPLOYEE_PICTURES } from './eager-employee-pictures.token';
import { EMPLOYEE_PICTURE_CACHE } from './employee-picture-cache';

@Component({
  selector: 'coin-employee-picture',
  imports: [AsyncPipe],
  templateUrl: './employee-picture.component.html',
  styleUrl: './employee-picture.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmployeePictureComponent {
  private readonly http = inject(HttpClient);
  protected readonly isEager = inject(EAGER_EMPLOYEE_PICTURES);

  readonly gid = input.required<string>();
  readonly size = input<PictureSize>('medium');
  readonly background = input<PictureBackground>('grey');
  readonly roundedBorder = input(true);

  protected readonly image$ = toObservable(this.gid).pipe(switchMap(gid => this.getImage(gid)));
  // Feeds the one-shot `when` trigger, so a plain cache lookup is enough: cached or in-flight gids skip the viewport wait.
  protected readonly isCached = computed(() => !!EMPLOYEE_PICTURE_CACHE[this.gid()]);

  protected readonly userPlaceholder = 'assets/images/user-placeholder.svg';

  private getImage(gid: string): Observable<string | null> {
    if (!gid) return of(this.userPlaceholder);
    if (EMPLOYEE_PICTURE_CACHE[gid]) return EMPLOYEE_PICTURE_CACHE[gid];

    const params = new HttpParams({ fromObject: { gid } });
    const image$ = this.http
      .get<{ img: string }>(`${environment.api.imagesService}images/employee-picture`, { params })
      .pipe(
        map(response => response.img),
        catchError(() => of(null)),
        share({
          connector: () => new ReplaySubject(0),
          resetOnError: false,
          resetOnComplete: () => timer(600_000).pipe(tap(() => delete EMPLOYEE_PICTURE_CACHE[gid])), // reset observable and clear cache after 10min (just for smoother in-memory loading; service worker already handles caching)
          resetOnRefCountZero: false
        })
      );
    EMPLOYEE_PICTURE_CACHE[gid] = image$;
    return image$;
  }
}

export const PICTURE_SIZES = ['tiny', 'small', 'medium', 'large', 'full'] as const;
export type PictureSize = (typeof PICTURE_SIZES)[number];

export const PICTURE_BACKGROUNDS = ['none', 'grey'] as const;
export type PictureBackground = (typeof PICTURE_BACKGROUNDS)[number];
